import { ReactNode, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import createPersistedState from 'use-persisted-state';
import { ValuesType } from 'utility-types';
import { v4 } from 'uuid';

import { GAME_MODE, PlayerSetup, SingSetup, SongPreview } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import { NavButton, NavRemoteControl } from '~/modules/elements/nav-controls';
import { GameModeIcon } from '~/modules/elements/people-glyph';
import styles from '~/modules/game-engine/drawing/styles';
import InputManager from '~/modules/game-engine/input/input-manager';
import gameEvents from '~/modules/game-events/game-events';
import { useEventEffect, useEventListenerSelector } from '~/modules/game-events/hooks';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import { useOnlineSongSelection } from '~/modules/online/song-selection-context';
import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { useQueuePosition } from '~/modules/song-queue/hooks';
import SongQueue from '~/modules/song-queue/song-queue';
import { trackLabel } from '~/modules/songs/utils/track-singers';
import { nextIndex, nextValue } from '~/modules/utils/indexes';
import isE2E from '~/modules/utils/is-e2-e';
import { ordinal } from '~/modules/utils/ordinal';
import { SONG_SETTINGS_TITLE } from '~/routes/keyboard-help/screen-titles';
import SelectInputModal from '~/routes/select-input/select-input-modal';
import {
  DebugDifficultiesSetting,
  MicSetupPreferenceSetting,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';
import DifficultyDots from '~/routes/sing-a-song/song-selection/components/song-settings/difficulty-dots';
import {
  clampTolerance,
  difficultyLevels,
  difficultyOptionOrder,
} from '~/routes/sing-a-song/song-selection/components/song-settings/difficulty-levels';
import SeriesLane from '~/routes/sing-a-song/song-selection/components/song-settings/series-lane';

interface Props {
  songPreview: SongPreview;
  onNextStep: (setup: SingSetup) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  /** The room's singers: the local mic check, or the online room's own panel. */
  singers: ReactNode;
  /** Swaps the open card over to another song - what the series lane picks. */
  onSelectSong: (songId: string) => void;
}

const gameModeNames = {
  [GAME_MODE.DUEL]: 'Duel',
  [GAME_MODE.PASS_THE_MIC]: 'Pass The Mic',
  [GAME_MODE.CO_OP]: 'Cooperation',
};

/** What the mode actually does to the scoring, which is the part the name does not say. */
const gameModeDescriptions = {
  // Up to four sing a duel, so it is a score each rather than two.
  [GAME_MODE.DUEL]: 'a score each',
  [GAME_MODE.PASS_THE_MIC]: 'take turns',
  [GAME_MODE.CO_OP]: 'one shared score',
};

const GAME_MODES = Object.values(GAME_MODE);

// added -v3 to the key as the value to handle default selection if it wasnt changed
const useSetGameMode = createPersistedState<ValuesType<typeof GAME_MODE> | null>('song_settings-game_mode-v3');
const useSetTolerance = createPersistedState<number>('song_settings-tolerance-v2');

// The pointer's own hint, and never on the control the keyboard is already sitting on: that one
// says "Enter will act on this" in the accent, and a cursor left lying on a TV must not overwrite
// that with white (see index.css). Dropped under e2e for the reason `ButtonBase` drops its own
// focus styling - a ring that depends on where the mouse was last left is not a stable baseline.
const hoverHint = (focused: boolean) => (isE2E() || focused ? '' : 'hover:hover-hint');

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-sm tracking-[0.08em] text-white/45 uppercase sm:text-base">{children}</span>
);

export default function GameSettings({
  songPreview,
  onNextStep,
  keyboardControl,
  onExitKeyboardControl,
  singers,
  onSelectSong,
}: Props) {
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const [storedPreference] = useSettingValue(MicSetupPreferenceSetting);
  const [rememberedMode, setMode] = useSetGameMode(null);
  const online = useOnlineSongSelection();
  const queuePosition = useQueuePosition(songPreview.id);
  const isQueued = queuePosition > 0;
  // Online play only supports Duel for now
  const mode = online
    ? GAME_MODE.DUEL
    : (rememberedMode ?? (songPreview.tracksCount > 1 ? GAME_MODE.CO_OP : GAME_MODE.DUEL));
  const [storedTolerance, setTolerance] = useSetTolerance(1);
  const [debugDifficulties] = useSettingValue(DebugDifficultiesSetting);
  // Memoised off the setting rather than built once at import time: the setting can be turned off
  // between two openings of the card, and a module-level array cannot follow it.
  const difficultyNames = useMemo(() => difficultyLevels(debugDifficulties), [debugDifficulties]);
  const tolerance = clampTolerance(storedTolerance, difficultyNames);

  useEffect(() => {
    online?.onPreviewSettingsChange(songPreview, difficultyNames[tolerance]);
  }, [songPreview, tolerance, difficultyNames, online]);

  // The singers the round will start with, not every slot: a slot still on the placeholder input is
  // dropped when the round starts, so counting it hid these pickers from two real singers - who
  // then both sang the first part.
  const players = PlayersManager.getSingingPlayers();
  // Exactly two singers, because that is the only count whose choice is read: `isMergedTrack` is
  // `players.length !== 2`, so at three or four everybody sings the merged track whatever these
  // pickers say. Offered more widely they were a control that looked like it did something.
  const multipleTracks = !mobilePhoneMode && players.length === 2 && songPreview.tracksCount > 1;

  // Read through an event selector rather than off `players`: who is on a slot is set on the mic
  // setup screen, which opens over this card, and `playerNameChanged` is the only event that says a
  // name moved - without it the pickers would keep showing whoever was on the slot when the card
  // opened. There is no "sings as" row here to make an assignment: naming a singer belongs with
  // choosing their microphone, and these pickers show the result of it.
  const slotNames = useEventListenerSelector(
    [gameEvents.playerNameChanged, gameEvents.playerInputChanged, gameEvents.playerAdded, gameEvents.playerRemoved],
    () =>
      PlayersManager.getSingingPlayers().map((player) => ({
        number: player.number,
        name: player.getName(),
      })),
  );

  // A part already picked is kept while the same number are singing, so a phone reconnecting while
  // this is open does not undo the choice. Not across a change of count: the parts picked for three
  // singers were no choice at all - everybody on the first.
  const initialisePlayerSetup = (previous: PlayerSetup[] = []) => {
    const currentPlayers = PlayersManager.getSingingPlayers();
    const hasMultipleTracks = !mobilePhoneMode && currentPlayers.length === 2 && songPreview.tracksCount > 1;
    const kept = previous.length === currentPlayers.length ? previous : [];

    return currentPlayers.map((player, index) => ({
      number: player.number,
      track: hasMultipleTracks
        ? (kept.find((setup) => setup.number === player.number)?.track ?? Math.min(index, songPreview.tracksCount - 1))
        : 0,
    }));
  };
  const [playerSetup, setPlayerSetup] = useState<PlayerSetup[]>(() => initialisePlayerSetup());
  // `playerInputChanged` as well: a phone taking over a placeholder slot changes who sings without
  // adding or removing anybody.
  useEventEffect(
    [gameEvents.playerAdded, gameEvents.playerRemoved, gameEvents.playerInputChanged],
    () => setPlayerSetup((current) => initialisePlayerSetup(current)),
    [mobilePhoneMode, songPreview.tracksCount],
  );

  const [showModal, setShowModal] = useState(false);
  useEffect(() => {
    if (!showModal) InputManager.startMonitoring();
  }, [showModal]);

  const areInputsConfigured = !!storedPreference && storedPreference !== 'skip';
  const canPlay = areInputsConfigured || online !== null;

  const handlePlay = () => onNextStep({ id: v4(), players: playerSetup, mode, tolerance: tolerance + 1 });

  // Same setup, but nothing sung is recorded - see the `practice` flag on `SingSetup`.
  const handlePractice = () =>
    onNextStep({ id: v4(), players: playerSetup, mode, tolerance: tolerance + 1, practice: true });

  // Queued with the settings showing right now, so a duet can sit between two solos without
  // anyone touching the settings again when its turn comes. Toggling, so a song queued by mistake
  // can be taken back out without going to find it in the panel.
  const handleQueue = () => {
    if (isQueued) {
      SongQueue.remove(songPreview.id);
      return;
    }

    SongQueue.add(songPreview, { mode, tolerance: tolerance + 1, players: playerSetup });
  };

  const changeMode = () => setMode(nextValue(GAME_MODES, mode));
  const changeTolerance = () => setTolerance(nextIndex(difficultyNames, tolerance, -1));

  // Every option drawn is an option that can be picked outright, by either input. The row used to be
  // a single focus stop that Enter cycled - which is the switcher this design says it is not, and it
  // also meant the ring glowed around all three while only one of them was what Enter would do.
  // `register` moves the selection onto whatever was activated, so no parking is needed here; the
  // click is stopped from bubbling because the card underneath reads one as "collapse me".
  const pickMode = (option: ValuesType<typeof GAME_MODE>) => (event?: Event) => {
    event?.stopPropagation();
    setMode(option);
  };

  const pickDifficulty = (name: string) => (event?: Event) => {
    event?.stopPropagation();
    setTolerance(difficultyNames.indexOf(name));
  };

  const pickModeFromRemote = (value: string) => {
    const option = GAME_MODES.find((candidate) => candidate === value);
    if (option) setMode(option);
  };

  const pickDifficultyFromRemote = (value: string) => {
    const index = difficultyNames.indexOf(value);
    if (index !== -1) setTolerance(index);
  };

  const song = useMemo(
    () => ({
      id: songPreview.id,
      title: songPreview.title,
      artist: songPreview.artist,
      duration: songPreview.duration,
      duet: songPreview.tracksCount > 1,
      video: songPreview.video || undefined,
    }),
    [songPreview],
  );

  const toggleTrack = (playerNumber: PlayerNumber) => () =>
    setPlayerSetup((current) =>
      current.map((s) => (s.number === playerNumber ? { ...s, track: (s.track + 1) % songPreview.tracksCount } : s)),
    );

  // Hints along the bottom rather than in the corner: the corner box is fixed top right at z-1000,
  // which on a card this wide is exactly where the last mode sits, and it would cover it.
  const { register, focusElement, focused } = useKeyboardNav({
    enabled: keyboardControl && !showModal,
    // Six options across two rows are columns, not positions in a list: `vertical` binds up and down
    // only, so every option but the first in each row would have no key that reaches it.
    direction: 'grid',
    onBackspace: onExitKeyboardControl,
    additionalHelp: { remote: ['select-song'] },
    title: SONG_SETTINGS_TITLE,
    song,
    // `select-inputs-button` registers first and used to take the fill for it - the microphone setup
    // rather than the song. Practice and Queue are alternatives to this one, not to each other.
    primary: 'play-song-button',
    presentation: 'strip',
  });

  // The phone keeps ONE row per setting rather than one per option: it is held at arm's length, and
  // six taps where there was one switch is worse, not better. Registered here in the body and not as
  // a <NavRemoteControl> child, because the mirrored order follows the order `register` is CALLED in
  // and every child registers after this whole function has run - as children these two would land
  // below the track rows on the phone.
  //
  // Online play is locked to Duel, so it gets no mode row at all, on the phone or as a focus stop:
  // a control the host cannot change should not take either. The wrapper keeps the test id.
  if (!online) {
    register('game-mode-setting', changeMode, 'Mode', false, {
      remoteOnly: true,
      control: {
        type: 'select' as const,
        label: 'Mode',
        value: mode,
        options: GAME_MODES.map((option) => ({
          value: option,
          label: gameModeNames[option],
          note: gameModeDescriptions[option],
        })),
      },
      onValueChange: pickModeFromRemote,
    });
  }

  register('difficulty-setting', changeTolerance, 'Difficulty', false, {
    remoteOnly: true,
    control: {
      type: 'select' as const,
      label: 'Difficulty',
      value: difficultyNames[tolerance],
      options: difficultyOptionOrder(difficultyNames).map((name) => ({ value: name, label: name })),
    },
    onValueChange: pickDifficultyFromRemote,
  });

  const setupMicsButton = !online && (
    <NavButton
      name="select-inputs-button"
      size={canPlay ? 'small' : 'large'}
      className={canPlay ? 'mobile:px-6 h-16! shrink-0 px-6' : 'mobile:px-6 shrink-0 px-9'}
      remoteIcon="settings"
      remoteLabel="Setup microphones"
      remoteNote={`${slotNames.length} of ${LOCAL_PLAYER_NUMBERS.length}`}
      // With nothing configured this is the only thing on the card worth pressing, so it is where
      // the keyboard lands - the empty state IS the primary action.
      isDefault={!canPlay}
      onClick={() => setShowModal(true)}>
      Setup mics
    </NavButton>
  );

  return (
    <>
      {createPortal(
        <SelectInputModal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            if (areInputsConfigured) focusElement('play-song-button');
          }}
        />,
        document.body,
      )}
      <KeyboardNavContext value={register}>
        {/* Every region below is a grid item of the card's own layout (see `song-preview-layout`),
            so the settings sit beside the video while the rest spans the full width underneath. */}
        <div className="contents">
          {/* Dimmed rather than disabled when nothing is set up: the settings still remember what
              they are set to, and the empty state below is what the eye should land on. */}
          <div className={`flex min-w-0 flex-col gap-4 transition-opacity ${canPlay ? '' : 'opacity-45'}`}>
            <div className="flex flex-col gap-2">
              <SectionLabel>Mode</SectionLabel>
              {/* All three at once, in the app's own people glyphs: two figures in two colours for a
                  duel, three for pass the mic, two in the SAME colour for co-op - which is the whole
                  difference between co-op and a duel, and what the names alone never said. */}
              <div data-test="game-mode-setting" data-test-value={gameModeNames[mode]} className="flex gap-3 rounded">
                {GAME_MODES.map((option) => {
                  const optionProps = online
                    ? { 'data-test': `game-mode-option-${option}` }
                    : register(`game-mode-option-${option}`, pickMode(option), gameModeNames[option], false, {
                        hideOnRemote: true,
                      });
                  const optionFocused = 'focused' in optionProps && optionProps.focused;

                  return (
                    <div
                      key={option}
                      {...optionProps}
                      data-mode={option}
                      data-selected={option === mode}
                      // One focus colour everywhere, because the ring is what means "Enter acts here"
                      // (see index.css). The picked option is filled with the accent already, so on
                      // that one the ring needs a dark inner edge to separate it - yellow laid
                      // straight onto amber is a border, not a ring, and says nothing at TV distance.
                      className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-2.5 sm:gap-2 sm:py-3 lg:h-[6.5rem] ${
                        online ? '' : 'cursor-pointer'
                      } ${option === mode ? 'bg-active text-black' : 'shadow-focusable bg-black/55 text-white'} ${
                        optionFocused
                          ? option === mode
                            ? 'shadow-[inset_0_0_0_2px_#facc15,inset_0_0_0_4px_#0000008c]'
                            : 'subtle-focus'
                          : ''
                      } ${online ? '' : hoverHint(optionFocused)}`}>
                      <GameModeIcon mode={option} height={26} />
                      <span className="flex max-w-full min-w-0 flex-col items-center gap-px text-center">
                        <span className="sm:text-md max-w-full truncate text-[0.6875rem] leading-tight font-bold tracking-[0.02em] uppercase lg:text-[1.1875rem]">
                          {gameModeNames[option]}
                        </span>
                        <span
                          className={`max-w-full truncate text-[0.625rem] sm:text-xs lg:text-sm ${option === mode ? 'opacity-60' : 'opacity-45'}`}>
                          {gameModeDescriptions[option]}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <SectionLabel>Difficulty</SectionLabel>
              <div
                data-test="difficulty-setting"
                data-test-value={difficultyNames[tolerance]}
                className="shadow-focusable flex overflow-hidden rounded">
                {difficultyOptionOrder(difficultyNames).map((name) => {
                  const selected = name === difficultyNames[tolerance];
                  const optionProps = register(`difficulty-option-${name}`, pickDifficulty(name), name, false, {
                    hideOnRemote: true,
                  });

                  return (
                    <div
                      key={name}
                      {...optionProps}
                      data-difficulty={name}
                      data-selected={selected}
                      className={`sm:text-md flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-3 truncate px-1 text-xs font-bold uppercase sm:h-13 lg:h-[3.875rem] lg:text-[1.3125rem] ${
                        selected ? 'bg-active text-black' : 'bg-black/55 text-white/80'
                      } ${
                        optionProps.focused
                          ? selected
                            ? 'shadow-[inset_0_0_0_2px_#facc15,inset_0_0_0_4px_#0000008c]'
                            : 'subtle-focus'
                          : ''
                      } ${hoverHint(optionProps.focused)}`}>
                      <DifficultyDots level={name} selected={selected} />
                      {name}
                    </div>
                  );
                })}
              </div>
            </div>

            {multipleTracks && (
              <div className="flex flex-col gap-2">
                <SectionLabel>Tracks · one per singer</SectionLabel>
                {slotNames.map((slot, index) => {
                  const setup = playerSetup.find((s) => s.number === slot.number) ?? { track: 0 };
                  const name = slot.name;
                  const trackProps = register(
                    `player-${slot.number}-track-setting`,
                    toggleTrack(slot.number as PlayerNumber),
                    `${name} track`,
                    false,
                    {
                      control: {
                        type: 'switch' as const,
                        label: name,
                        value: trackLabel(songPreview, setup.track),
                      },
                    },
                  );

                  return (
                    <div
                      key={slot.number}
                      {...trackProps}
                      data-test-value={setup.track + 1}
                      className={`shadow-focusable text-md flex h-12 cursor-pointer items-center justify-between rounded bg-black/55 px-3.5 font-bold uppercase sm:h-[3.375rem] sm:px-4 sm:text-lg ${
                        trackProps.focused ? 'subtle-focus' : ''
                      } ${hoverHint(trackProps.focused)}`}>
                      <span className="ph-no-capture truncate" style={{ color: styles.colors.players[index]?.text }}>
                        {name}
                      </span>
                      <span className="shrink-0 pl-3 text-white">{trackLabel(songPreview, setup.track)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* The singers row sits outside the branch below on purpose: the mic check starts the audio
              pipeline when it mounts and stops it again when it unmounts, so moving it between two
              branches as microphones are configured would tear down the monitoring that setting them
              up had just started - the newly connected phone would go quiet the moment it arrived. */}
          <div
            className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 lg:col-span-2 ${
              canPlay ? '' : 'opacity-50'
            }`}>
            <div className="min-w-0 flex-1">{singers}</div>
            {canPlay && setupMicsButton}
          </div>

          {canPlay ? (
            <>
              {/* Fixed on mobile — the expanded card scrolls there, and its natural spot in the list
                  can sit below the fold, so `sticky` alone wouldn't show it until the user scrolled
                  down past it. Pin it to the viewport instead so it's visible from the start.
                  Desktop's card never scrolls that far, so this reverts to normal flow. */}
              <div className="fixed inset-x-px bottom-0 z-40 flex h-[3.75rem] items-center gap-2 bg-slate-800 px-3 sm:static sm:h-auto sm:gap-3.5 sm:bg-transparent sm:p-0 lg:col-span-2">
                <NavButton
                  name="play-song-button"
                  size="large"
                  className="mobile:px-10 mobile:h-10 mobile:text-md w-full flex-2 px-20 py-1 sm:h-[4.75rem]! sm:w-auto"
                  remoteIcon="play"
                  isDefault
                  onClick={handlePlay}>
                  Play
                </NavButton>
                {/* Online rooms are a shared performance - practising alone in one makes no sense. */}
                {!online && (
                  <NavButton
                    name="practice-song-button"
                    size="large"
                    className="mobile:px-4 mobile:h-10 mobile:text-md flex-1 px-8 py-1 sm:h-[4.75rem]!"
                    remoteIcon={null}
                    onClick={handlePractice}>
                    Practice
                  </NavButton>
                )}
                {/* Online rooms run off the host's picks and votes, not a local running order. */}
                {!online && (
                  <NavButton
                    name="queue-song-button"
                    size="large"
                    remoteLabel={isQueued ? `Queued ${ordinal(queuePosition)}` : 'Queue'}
                    // Green says "done" and the ring says "Enter would act on this" — once queued the
                    // button keeps its own colour and shows focus as a ring, because the accent fill
                    // would take the green with it and leave nothing saying the song is in.
                    subtleFocused={isQueued}
                    className={`mobile:px-4 mobile:h-10 mobile:text-md flex-1 px-8 py-1 sm:h-[4.75rem]! ${
                      isQueued ? 'bg-green-400/15! text-green-400! shadow-[inset_0_0_0_2px_#4ade80]' : ''
                    }`}
                    remoteIcon={null}
                    onClick={handleQueue}>
                    {/* The button is the confirmation - nothing else on screen says the add landed,
                        and the undo is the same key rather than a trip back to the queue. */}
                    {isQueued ? (
                      <span className="flex flex-col items-center gap-0.5 leading-tight">
                        <span className="flex items-center gap-2">
                          <Icon icon="ic:baseline-check" size={5} />
                          Queued · {ordinal(queuePosition)}
                        </span>
                        <span className="mobile:hidden text-xs font-semibold tracking-[0.06em] opacity-80">
                          press again to remove
                        </span>
                      </span>
                    ) : (
                      'Queue'
                    )}
                  </NavButton>
                )}
              </div>
            </>
          ) : (
            // Standing where the Play row would be, because with nothing set up this IS the action.
            <div className="flex flex-col gap-4 rounded-2xl bg-black/35 p-4 sm:flex-row sm:items-center sm:p-6 lg:col-span-2">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="stroke-text text-xl font-bold sm:text-[1.75rem]">Microphones are not set up yet</span>
                <span className="text-md text-white/60 sm:text-xl">
                  Nothing can be scored until at least one is. Phones work as microphones too.
                </span>
              </div>
              {setupMicsButton}
            </div>
          )}

          <SeriesLane song={songPreview} focused={focused === 'series-songs'} onSelect={onSelectSong} />

          {/* Clears the action bar, which is fixed to the bottom of the viewport on mobile and would
              otherwise cover the end of the card. Last, so everything above it can be scrolled to. */}
          {canPlay && <div aria-hidden className="h-[3.75rem] sm:hidden" />}

          {/* Remote-only: the expanded song card has no back button on desktop (only the Backspace
              handler and a mobile-only one in song-preview), so the phone would otherwise have no way
              out of this screen once mirrored. */}
          <NavRemoteControl
            name="exit-song-settings"
            control={{ type: 'button', label: 'Back to song list', variant: 'back' }}
            onClick={onExitKeyboardControl}
          />
        </div>
      </KeyboardNavContext>
    </>
  );
}
